import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SocialShare from "../components/SocialShare";
import { getArticle, getArticles } from "../lib/api";
import { ArrowLeft, Calendar, Eye, Sparkles, ExternalLink } from "lucide-react";
import ArticleCard from "../components/ArticleCard";

function updateMetaTags(article) {
  if (!article) return;
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const imgUrl = article.image_url?.startsWith("/api/")
    ? `${API}${article.image_url}`
    : article.image_url || "";

  const setMeta = (attr, key, content) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };

  document.title = `${article.title} - ArrochaPlay`;
  setMeta("property", "og:title", article.title);
  setMeta("property", "og:description", (article.excerpt || "").slice(0, 200));
  setMeta("property", "og:image", imgUrl);
  setMeta("property", "og:type", "article");
  setMeta("property", "og:url", window.location.href);
  setMeta("name", "twitter:title", article.title);
  setMeta("name", "twitter:description", (article.excerpt || "").slice(0, 200));
  setMeta("name", "twitter:image", imgUrl);
}

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    
    const fetchArticle = async () => {
      try {
        const response = await getArticle(id);
        setArticle(response.data);
        updateMetaTags(response.data);

        // Fetch related articles from same category
        const relatedResponse = await getArticles({ 
          category: response.data.category, 
          status: "published",
          limit: 4 
        });
        setRelatedArticles(
          relatedResponse.data.filter(a => a.id !== id).slice(0, 3)
        );
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 pt-32 text-center">
          <h1 className="font-heading text-3xl font-bold text-white mb-4">
            Artigo não encontrado
          </h1>
          <Link to="/" className="text-[#FF007F] hover:underline">
            Voltar para o início
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const currentUrl = window.location.href;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      <main className="pt-24">
        {/* Hero Image - retrato preserva proporção; paisagem preenche */}
        {article.image_url && article.image_url.length > 1 && (
          <div className="relative w-full overflow-hidden bg-black">
            <img
              src={article.image_url}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-40 pointer-events-none"
              loading="lazy"
            />
            <img
              src={article.image_url}
              alt={article.title}
              className="relative w-full max-h-[60vh] sm:max-h-[500px] object-contain mx-auto"
              loading="eager"
            />
          </div>
        )}

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 mt-8 relative z-10">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#FF007F] transition-colors mb-6"
            data-testid="back-link"
          >
            <ArrowLeft size={18} />
            Voltar
          </Link>

          {/* Category & AI Badge */}
          <div className="flex items-center gap-3 mb-4">
            <Link
              to={`/categoria/${encodeURIComponent(article.category)}`}
              className="category-pill hover:border-[#FF007F] transition-colors"
            >
              {article.category}
            </Link>
          </div>

          {/* Title */}
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-zinc-400 text-sm mb-8 pb-8 border-b border-white/10">
            <span className="flex items-center gap-2 text-[#FF007F] font-semibold" data-testid="article-author">
              Por Equipe Arrocha Play
            </span>
            <span className="flex items-center gap-2">
              <Calendar size={16} />
              {formatDate(article.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye size={16} />
              {article.views} visualizações
            </span>
            <SocialShare url={currentUrl} title={article.title} />
          </div>

          {/* Excerpt */}
          <p className="text-xl text-zinc-300 leading-relaxed mb-8 font-medium">
            {article.excerpt}
          </p>

          {/* Content */}
          <div 
            className="prose-dark text-zinc-400 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            data-testid="article-content"
          />

          {/* Share Section */}
          <div className="mt-12 py-8 border-t border-white/10">
            <h3 className="font-heading text-lg font-bold text-white mb-4">
              Gostou? Compartilhe!
            </h3>
            <SocialShare url={currentUrl} title={article.title} />
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-heading text-2xl font-bold text-white mb-8">
              Mais em {article.category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <ArticleCard key={related.id} article={related} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
